import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { useAppDispatch } from "@/redux/hooks";
import { restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { ClientBooking } from "../Client/ClientBooking";
import { AuthedWidgetRedirect } from "./AuthedWidgetRedirect";
import { BookingUnavailable } from "./BookingUnavailable";
import { GuestBookingProvider } from "./GuestBookingContext";
import { clearStashedGuestDraft, readStashedGuestDraft } from "./guestDraftStash";
import { useHydrateBookingFromQuote } from "./useHydrateBookingFromQuote";

/**
 * Entry point for the widget quote handoff (`/guest/booking?widget=v1&quote_uuid=…`).
 *
 * The widget URL carries NO operator code and no session assumption. If the visitor is already logged
 * in, they should NOT be dropped into the anonymous guest flow — they're routed to the authenticated
 * booking flow (prefilled from the quote) by `AuthedWidgetRedirect`. Only a logged-out visitor runs the
 * guest flow below.
 */
export const GuestBooking = () => {
  const { isAuthenticated, isLoading } = useKindeAuth();
  const [searchParams] = useSearchParams();
  const quoteUuid = searchParams.get("quote_uuid") ?? undefined;

  // Wait for Kinde to finish hydrating before deciding guest-vs-authed (mirrors RequireAuth).
  if (isLoading) return <Loading />;
  if (isAuthenticated) return <AuthedWidgetRedirect quoteUuid={quoteUuid} />;
  return <GuestBookingFlow quoteUuid={quoteUuid} />;
};

/**
 * The unauthenticated guest flow. Resolves the operator FROM the quote and rehydrates the shared draft
 * from it (via `useHydrateBookingFromQuote`), then renders the EXISTING client booking wizard wrapped in
 * `GuestBookingProvider` so `useGuestBooking().isGuest` is true throughout its subtree — the guest-specific
 * behaviour (guest v3 job-data / stripe keys / create-submit / self passenger) hangs off that seam. The
 * authenticated path never wraps the wizard, so `isGuest` defaults to false there.
 */
const GuestBookingFlow = ({ quoteUuid }: { quoteUuid?: string }) => {
  const dispatch = useAppDispatch();

  // A stashed draft (guest returning from an ACCOUNT_EXISTS 409 → login) wins over the quote prefill.
  // Captured at mount so quote hydration is skipped for the whole lifetime when a stash was present.
  const hadStash = useRef(readStashedGuestDraft() != null);
  const { status } = useHydrateBookingFromQuote({ quoteUuid, resolveOperator: true, skipHydration: hadStash.current });

  useEffect(() => {
    const stashed = readStashedGuestDraft();
    if (stashed) {
      dispatch(restoreBookingDraft(stashed));
      clearStashedGuestDraft();
    }
  }, [dispatch]);

  if (status === "loading") return <Loading />;
  if (status === "not-found") return <BookingUnavailable />;

  // Full-height flex column so ClientBooking's `flex-1 @container` fills the viewport and its
  // container-query breakpoints hit the desktop (3-panel) layout — mirrors ClientPortal's content
  // wrapper. Without this, the bare wizard collapses to its narrow layout ("half screen").
  return (
    <GuestBookingProvider>
      <div className="flex h-screen flex-col">
        <main className="flex min-h-0 flex-1 flex-col overflow-auto bg-neutral-surface-gray">
          <ClientBooking />
        </main>
      </div>
    </GuestBookingProvider>
  );
};
